/**
 * CoverLetterPage -- AI-powered cover letter generation.
 *
 * Users paste their resume text and a job description, then get a tailored
 * cover letter with optional personalization. The page supports two modes:
 *
 *  - **Streaming (SSE)** (default): text appears progressively via
 *    `useAiStream` driving `generateCoverLetterStream()`. Readonly while
 *    streaming, editable once the `done` event arrives. Cancel button
 *    aborts mid-stream.
 *
 *  - **Non-streaming**: legacy single-shot `aiApi.generateCoverLetter()`
 *    call. Kept for users on constrained networks where chunked transfer
 *    encoding is unreliable.
 */
import { useCallback, useMemo, useState } from 'react';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { AiAssistButton } from '../../components/cms/AiAssistButton';
import { AiSuggestionPanel } from '../../components/cms/AiSuggestionPanel';
import { useAiAssist } from '../../hooks/useAiAssist';
import { useAiStream } from '../../hooks/useAiStream';
import {
  aiApi,
  generateCoverLetterStream,
  type CoverLetterStreamEvent,
} from '../../services/ai.api';
import type {
  GenerateCoverLetterRequest,
  GenerateCoverLetterData,
} from '../../types/ai.types';

export function CoverLetterPage() {
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [hiringManager, setHiringManager] = useState('');
  const [tone, setTone] = useState<'formal' | 'conversational' | 'enthusiastic'>('formal');
  const [savedLetter, setSavedLetter] = useState('');
  const [copied, setCopied] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true);

  // --- Non-streaming path (original behaviour) ---
  const coverLetterAi = useAiAssist<
    GenerateCoverLetterRequest,
    GenerateCoverLetterData
  >({
    apiCall: (input) => aiApi.generateCoverLetter(input),
    onAccept: (data) => {
      setSavedLetter(data.content);
    },
  });

  // --- Streaming path (SSE) ---
  // Bind the stream call to the generic hook. We wrap generateCoverLetterStream
  // in a stable callback so useCallback inside the hook sees a consistent
  // reference across renders.
  const streamCall = useCallback(
    (
      req: GenerateCoverLetterRequest,
      signal: AbortSignal,
    ): AsyncIterable<CoverLetterStreamEvent> =>
      generateCoverLetterStream(req, signal),
    [],
  );
  const stream = useAiStream<GenerateCoverLetterRequest>(streamCall);

  const canGenerate =
    resumeText.trim().length >= 50 && jobDescription.trim().length >= 20;

  const currentRequest = useMemo<GenerateCoverLetterRequest>(
    () => ({
      resume_text: resumeText.trim(),
      job_description: jobDescription.trim(),
      company_name: companyName.trim() || undefined,
      hiring_manager: hiringManager.trim() || undefined,
      tone,
    }),
    [resumeText, jobDescription, companyName, hiringManager, tone],
  );

  async function handleCopy() {
    const textToCopy = useStreaming ? stream.text : savedLetter;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the textarea
    }
  }

  function handleGenerate(): void {
    if (useStreaming) {
      void stream.trigger(currentRequest);
    } else {
      void coverLetterAi.trigger(currentRequest);
    }
  }

  function handleAcceptStreaming(): void {
    setSavedLetter(stream.text);
    stream.reset();
  }

  // While streaming, show accumulating text. After done, allow editing.
  const streamingActive = useStreaming && (stream.isStreaming || stream.text.length > 0);

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Cover Letter</h1>
        <div className="ai-label-row">
          <label className="input-label" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={useStreaming}
              onChange={(e) => setUseStreaming(e.target.checked)}
              disabled={stream.isStreaming || coverLetterAi.isLoading}
            />{' '}
            Stream output
          </label>
          <AiAssistButton
            size="md"
            onClick={handleGenerate}
            isLoading={useStreaming ? stream.isStreaming : coverLetterAi.isLoading}
            disabled={!canGenerate}
            label="Generate cover letter with AI"
          />
        </div>
      </header>

      <Panel>
        <Panel.Header>Inputs</Panel.Header>
        <Panel.Content>
          <div className="input-wrapper">
            <label className="input-label">Resume Text</label>
            <textarea
              className="input input--textarea"
              rows={8}
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste your resume text here (minimum 50 characters)..."
            />
          </div>
          <div className="input-wrapper">
            <label className="input-label">Job Description</label>
            <textarea
              className="input input--textarea"
              rows={6}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the target job description here (minimum 20 characters)..."
            />
          </div>
          <div className="experience-entry__row">
            <div className="input-wrapper" style={{ flex: 1 }}>
              <label className="input-label">Company Name (optional)</label>
              <input
                className="input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Acme Corp"
              />
            </div>
            <div className="input-wrapper" style={{ flex: 1 }}>
              <label className="input-label">Hiring Manager (optional)</label>
              <input
                className="input"
                value={hiringManager}
                onChange={(e) => setHiringManager(e.target.value)}
                placeholder="e.g. Jane Smith"
              />
            </div>
            <div className="input-wrapper">
              <label className="input-label">Tone</label>
              <select
                className="input"
                value={tone}
                onChange={(e) =>
                  setTone(e.target.value as 'formal' | 'conversational' | 'enthusiastic')
                }
              >
                <option value="formal">Formal</option>
                <option value="conversational">Conversational</option>
                <option value="enthusiastic">Enthusiastic</option>
              </select>
            </div>
          </div>
        </Panel.Content>
      </Panel>

      {/* --- Streaming result panel --- */}
      {streamingActive && (
        <Panel>
          <Panel.Header>
            <div className="ai-label-row">
              {stream.isStreaming ? 'Generating…' : 'Generated Cover Letter'}
              {stream.isStreaming && (
                <Button variant="secondary" size="sm" onClick={stream.cancel}>
                  Cancel
                </Button>
              )}
              {!stream.isStreaming && stream.text.length > 0 && (
                <>
                  <Button variant="secondary" size="sm" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAcceptStreaming}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          </Panel.Header>
          <Panel.Content>
            <textarea
              className="input input--textarea"
              rows={15}
              value={stream.text}
              onChange={(e) => {
                // Only allow editing once the stream has completed.
                if (!stream.isStreaming) {
                  // Mutating state from an uncontrolled source isn't supported
                  // in this hook, so we drop changes during streaming and
                  // persist edits by saving to `savedLetter` via Save.
                  setSavedLetter(e.target.value);
                }
              }}
              readOnly={stream.isStreaming}
              placeholder="Cover letter will appear here as it's generated…"
            />
            {stream.error && (
              <p className="form-error" role="alert">
                {stream.error}
              </p>
            )}
            {!stream.isStreaming && stream.durationMs !== null && (
              <p className="list-empty">
                Generated in {(stream.durationMs / 1000).toFixed(1)}s
              </p>
            )}
          </Panel.Content>
        </Panel>
      )}

      {/* --- Non-streaming suggestion panel (legacy path) --- */}
      {!useStreaming && (
        <AiSuggestionPanel
          suggestion={
            coverLetterAi.suggestion ? coverLetterAi.suggestion.content : null
          }
          error={coverLetterAi.error}
          isLoading={coverLetterAi.isLoading}
          onAccept={coverLetterAi.accept}
          onDismiss={coverLetterAi.dismiss}
        />
      )}

      {/* --- Saved letter display (both modes) --- */}
      {savedLetter &&
        !streamingActive &&
        !coverLetterAi.suggestion &&
        !coverLetterAi.isLoading && (
          <Panel>
            <Panel.Header>
              <div className="ai-label-row">
                Saved Cover Letter
                <Button variant="secondary" size="sm" onClick={handleCopy}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </Panel.Header>
            <Panel.Content>
              <textarea
                className="input input--textarea"
                rows={15}
                value={savedLetter}
                onChange={(e) => setSavedLetter(e.target.value)}
              />
            </Panel.Content>
          </Panel>
        )}

      {!canGenerate && (resumeText.length > 0 || jobDescription.length > 0) && (
        <p className="list-empty">
          Resume (50+ chars) and job description (20+ chars) are both required.
        </p>
      )}
    </div>
  );
}
